import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Battery50Icon, BuildingStorefrontIcon, CheckIcon, ChevronDownIcon,
  MagnifyingGlassIcon, MinusIcon, PlusIcon, XMarkIcon,
} from '@heroicons/react/24/outline'
import IkonaFalownika from '../ikony/IkonaFalownika'
import IkonaModulu from '../ikony/IkonaModulu'
import * as api from '../../api/account'
import { recommendPanel } from '../../api/recommend'
import {
  getInverterManufacturerLogoPath, getMagazineManufacturerLogoPath, getPanelManufacturerLogoPath,
} from '../../utils'
// Rozwiązywanie slotów mieszka w `lib/slotyModulow`, bo tego samego kodu używa
// asystent doboru w trybie „TYLKO wybrane moduły" — dwie kopie rozjechałyby się
// przy pierwszej zmianie kryteriów.
import { PUSTY_SLOT, slotPusty, szukajModulu } from '../../lib/slotyModulow'
import { Button, ErrorText, SectionTitle } from './ui'
// Kafelek modułu jest WSPÓLNY z asystentem (components/kits/KafelekModulu):
// ten sam produkt ma wyglądać tak samo w preferencjach i przy doborze.
import KafelekModulu from '../kits/KafelekModulu'
import WyborSeriiIModeli from './WyborSeriiIModeli'

/**
 * Preferencje doboru — na jakim sprzęcie i z jakimi sklepami pracuje firma
 * (PLAN_SEGMENTY §5, Etap A).
 *
 * ═══ KOLEJNOŚĆ SEKCJI NIE JEST PRZYPADKOWA ═══
 * Falowniki i magazyny PIERWSZE — stabilna część rynku, konfiguruje się raz.
 * Moduły OSTATNIE i inaczej: rotują tygodniowo, więc zamiast konkretnych modeli
 * użytkownik ustawia SLOTY KRYTERIÓW („do 400 zł, od 500 W, Bifacial"), a my
 * pokazujemy najtańszy moduł spełniający każdy slot — z żywych cen i z realną
 * dostępnością. Model zapisany na sztywno byłby nieaktualny po tygodniu.
 *
 * Od 2026-10-09 (prośba właściciela) w każdej kategorii obok marek można wskazać
 * konkretne SERIE i MODELE (`WyborSeriiIModeli`, pole `wybrane`). Działają jak marki:
 * suma marek, serii i modeli to „mój” sprzęt, a „TYLKO” zawęża dobór do tej sumy.
 */

// Czy w kategorii wskazano cokolwiek poza markami.
const maWybrane = (prefs, kat) => {
  const w = prefs.wybrane?.[kat]
  return Boolean(w && (w.serie?.length || w.modele?.length))
}


// ── Chip wybranej pozycji: logo, gdy jest; nazwa, gdy loga brak ─────────────
// Logotypy leżą w public/*_manufacturers, ale nie dla wszystkich marek —
// dlatego fallback przez onError, a nie lista „które mamy" utrzymywana ręcznie.
const ChipWyboru = ({ nazwa, logoSrc, onRemove }) => {
  const [logoPadlo, setLogoPadlo] = useState(false)
  const zLogo = Boolean(logoSrc) && !logoPadlo

  if (!zLogo) {
    return (
      <span className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full
                       bg-blue-50 border border-blue-200 text-xs font-medium text-blue-800">
        {nazwa}
        <button type="button" onClick={onRemove} aria-label={`Usuń ${nazwa}`}
                className="p-0.5 rounded-full hover:bg-blue-100">
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </span>
    )
  }
  return (
    <span className="group relative inline-flex h-12 w-28 p-1.5 rounded-lg bg-white
                     border border-gray-200 hover:border-blue-300 transition-colors">
      <img src={logoSrc} alt={nazwa} title={nazwa}
           className="h-full w-full object-contain"
           onError={() => setLogoPadlo(true)} />
      {/* X w prawym górnym rogu, widoczny po najechaniu — i po fokusie
          klawiaturą, bo hover nie istnieje dla Tab. */}
      <button type="button" onClick={onRemove} aria-label={`Usuń ${nazwa}`}
              className="absolute -top-2 -right-2 p-0.5 rounded-full bg-white border
                         border-gray-300 shadow-sm text-gray-500 hover:text-red-600
                         opacity-0 group-hover:opacity-100 focus-visible:opacity-100
                         transition-opacity">
        <XMarkIcon className="w-3.5 h-3.5" />
      </button>
    </span>
  )
}

// ── Karty i pasek zakładek ───────────────────────────────────────────────────
// Każda grupa ustawień to osobna karta z niebieską ikoną w nagłówku — ten sam
// język wizualny co sekcje asystenta doboru (components/kits/CategorySection).
// Wcześniej wszystko leciało jednym ciągiem rozdzielonym kreskami i przy sześciu
// slotach modułów nie było widać, gdzie kończy się jedno ustawienie.

const ZAKLADKI = [
  { id: 'falowniki', label: 'Falowniki' },
  { id: 'magazyny', label: 'Magazyny' },
  { id: 'sklepy', label: 'Sklepy' },
  { id: 'moduly', label: 'Moduły' },
]

const KartaSekcji = ({ id, Icon, tytul, opis, children }) => (
  // `scroll-mt-32` odsuwa cel skoku spod paska nawigacji (h-16) i spod samego
  // paska zakładek — bez tego kliknięcie w zakładkę chowa nagłówek karty.
  <section id={id} className="scroll-mt-32 rounded-xl border border-blue-300 bg-white shadow-sm">
    <div className="flex items-start gap-2.5 px-4 md:px-5 pt-4 pb-2">
      <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center
                       rounded-lg bg-blue-50 text-blue-600">
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0">
        <h3 className="text-sm font-bold text-gray-900">{tytul}</h3>
        {opis && <p className="text-xs text-gray-500 leading-5 mt-0.5">{opis}</p>}
      </div>
    </div>
    <div className="px-4 md:px-5 pb-4">{children}</div>
  </section>
)

/**
 * Poziomy pasek skrótów do kart. Przykleja się pod paskiem nawigacji (`top-16`),
 * bo cała sekcja jest długa — przy slotach modułów rozwiniętych na trzy rzędy
 * powrót do producentów oznaczał przewijanie w ciemno.
 *
 * Ujemne marginesy zdejmują `p-5`/`p-6` kontenera konta: bez nich przyklejony
 * pasek zostawiałby po bokach szpary, przez które przejeżdżałaby treść.
 */
const PasekZakladek = ({ aktywna, onWybor }) => (
  <div className="sticky top-16 z-10 -mx-5 sm:-mx-6 px-5 sm:px-6 py-2
                  bg-white/95 backdrop-blur border-b border-gray-100">
    <div className="flex gap-1.5 overflow-x-auto">
      {ZAKLADKI.map(({ id, label }) => (
        <button
          key={id} type="button" onClick={() => onWybor(id)}
          aria-current={aktywna === id ? 'true' : undefined}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap
                      transition-colors focus:outline-none focus-visible:ring-2
                      focus-visible:ring-blue-500 ${
            aktywna === id
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  </div>
)

/**
 * Picker nazw: wyszukiwarka + PEŁNA lista do rozwinięcia. Sama wyszukiwarka
 * nie wystarcza — kto pracuje na czymś rzadko, nie przypomni sobie nazwy,
 * dopóki jej nie zobaczy.
 *
 * `tytul` jest opcjonalny: wewnątrz karty nazwę niesie już jej nagłówek,
 * a powtórzony niżej wyglądałby jak drugi, zagnieżdżony blok.
 */
const WyborNazw = ({ tytul, wstep, dostepne, wybrane, onChange, placeholder, logoPath, bezPelnejListy }) => {
  const [fraza, setFraza] = useState('')
  const [listaOtwarta, setListaOtwarta] = useState(false)

  const pasujace = useMemo(() => {
    const f = fraza.trim().toLowerCase()
    if (!f) return []
    return dostepne.filter((n) => n.toLowerCase().includes(f) && !wybrane.includes(n)).slice(0, 8)
  }, [fraza, dostepne, wybrane])

  const przelacz = (nazwa) => {
    onChange(wybrane.includes(nazwa) ? wybrane.filter((n) => n !== nazwa) : [...wybrane, nazwa])
    setFraza('')
  }

  return (
    <div>
      {tytul && <p className="text-sm font-semibold text-gray-900">{tytul}</p>}
      {wstep && <p className="text-xs text-gray-500 leading-5 mt-0.5 mb-2">{wstep}</p>}

      {wybrane.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2 mt-1">
          {wybrane.map((n) => (
            <ChipWyboru key={n} nazwa={n} logoSrc={logoPath ? logoPath(n) : null}
                        onRemove={() => przelacz(n)} />
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-64">
          <input
            value={fraza} onChange={(e) => setFraza(e.target.value)} placeholder={placeholder}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          {pasujace.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg
                           shadow-lg overflow-hidden">
              {pasujace.map((n) => (
                <li key={n}>
                  <button type="button" onClick={() => przelacz(n)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50">
                    {n}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Sklepy nie dostają pełnej listy: to katalog źródeł porównywarki,
            a wyliczanie go w ustawieniach wyglądałoby jak zaproszenie do
            odhaczenia wszystkich. Producenci tak — tam lista przypomina marki. */}
        {!bezPelnejListy && (
          <button type="button" onClick={() => setListaOtwarta((o) => !o)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700">
            {listaOtwarta ? 'Zwiń listę' : `Pokaż całą listę (${dostepne.length})`}
            <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${listaOtwarta ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {!bezPelnejListy && listaOtwarta && (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-gray-200 p-2
                        grid grid-cols-2 sm:grid-cols-3 gap-1">
          {dostepne.map((n) => {
            const aktywny = wybrane.includes(n)
            return (
              <button key={n} type="button" onClick={() => przelacz(n)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-left
                                  transition-colors ${aktywny
                                    ? 'bg-blue-50 text-blue-800 font-semibold'
                                    : 'text-gray-600 hover:bg-gray-50'}`}>
                <CheckIcon className={`w-3.5 h-3.5 flex-shrink-0 ${aktywny ? 'text-blue-600' : 'invisible'}`} />
                <span className="truncate">{n}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Tryb modułów: dwie opcje, które WYKLUCZAJĄ SIĘ nawzajem, i wolno nie wybrać
 * żadnej. Kwadraty, a nie kółka radia, bo radio nie pozwala się odznaczyć —
 * a „bez ograniczeń" jest tu pełnoprawnym, domyślnym stanem, nie brakiem odpowiedzi.
 *
 * Opcja bez danych jest WYŁĄCZONA, nie ukryta: znikający checkbox nie tłumaczy,
 * czego brakuje, żeby go użyć.
 */
const WyborTrybuModulow = ({ tryb, onChange, maProducentow, maSloty }) => {
  const OPCJE = [
    {
      value: 'manufacturers',
      label: <>Dobieraj <strong>TYLKO</strong> te marki, serie i modele modułów</>,
      aktywna: maProducentow,
      powod: 'Najpierw wybierz choć jednego producenta, serię albo model modułów.',
    },
    {
      value: 'slots',
      label: <>Dobieraj <strong>TYLKO</strong> wybrane moduły</>,
      aktywna: maSloty,
      powod: 'Najpierw ustaw kryteria choć jednego modułu.',
    },
  ]

  return (
    <div className="space-y-2">
      {OPCJE.map(({ value, label, aktywna, powod }) => (
        <label key={value}
               title={aktywna ? undefined : powod}
               className={`flex items-start gap-2.5 select-none w-fit ${
                 aktywna ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
          <input
            type="checkbox" disabled={!aktywna} checked={tryb === value}
            // Ponowne kliknięcie zaznaczonej opcji wraca do „bez ograniczeń";
            // kliknięcie drugiej przełącza, bo obie naraz dałyby przecięcie,
            // które prawie zawsze jest puste.
            onChange={(e) => onChange(e.target.checked ? value : 'any')}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            {label}
            {!aktywna && <span className="block text-xs text-gray-400 mt-0.5">{powod}</span>}
          </span>
        </label>
      ))}
      <p className="text-xs text-gray-400">
        {tryb === 'any'
          ? 'Bez zaznaczenia asystent dobiera też inne moduły, a z Twoich powstaje osobny wariant „Preferowane wybory".'
          : 'Zaznaczyć można tylko jedno. Odznacz, żeby wrócić do doboru bez ograniczeń.'}
      </p>
    </div>
  )
}

const CheckboxTylko = ({ checked, onChange, children }) => (
  <label className="flex items-start gap-2.5 cursor-pointer select-none w-fit">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
           className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
    <span className="text-sm text-gray-700">{children}</span>
  </label>
)

// ── Sloty modułów ────────────────────────────────────────────────────────────

const TYPY_MODULOW = ['Standard', 'Bifacial', 'Full Black']

const przelaczTyp = (typy, typ) => {
  if (typy.includes(typ)) return typy.filter((t) => t !== typ)
  // Standard wyklucza resztę — panel nie może być jednocześnie standardowy
  // i dwustronny. Bifacial + Full Black wolno łączyć.
  return typ === 'Standard' ? ['Standard'] : [...typy.filter((t) => t !== 'Standard'), typ]
}

const SlotModulu = ({ numer, slot, onChange, producenci, sklepy, minSztuk }) => {
  const [oferta, setOferta] = useState(null)
  const [szukam, setSzukam] = useState(true)
  const pusty = slotPusty(slot)

  // Podgląd przeliczany z opóźnieniem — każda literka w polu ceny to inaczej
  // osobne zapytanie do katalogu.
  const kluczKryteriow = JSON.stringify([slot, producenci, sklepy, minSztuk])
  useEffect(() => {
    let anulowane = false
    setSzukam(true)
    const t = setTimeout(async () => {
      // Pusty slot pokazuje produkt polecany przez VoltValue (ten sam mechanizm
      // co dymek w asystencie — konfiguracja proponowaneWAsystencie), a nie
      // „najtańszy cokolwiek": świeżo dodany slot ma zachęcać, nie straszyć
      // najtańszym rekordem z katalogu. Pierwsze wpisane kryterium przełącza
      // na normalne wyszukiwanie.
      const wynik = pusty
        ? await recommendPanel()
        : await szukajModulu(slot, producenci, sklepy, minSztuk)
      if (anulowane) return
      setOferta(wynik)
      setSzukam(false)
    }, 600)
    return () => { anulowane = true; clearTimeout(t) }
  }, [kluczKryteriow]) // eslint-disable-line react-hooks/exhaustive-deps

  const ustaw = (pole) => (e) => onChange({ ...slot, [pole]: e.target.value })
  const poleKlasa = 'w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'

  return (
    <div className="rounded-2xl border border-gray-200 p-3 space-y-2.5 bg-gray-50/40">
      <p className="text-xs font-bold text-gray-900">Moduł {numer}</p>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-[11px] text-gray-500 mb-1">Cena maks. (zł)</span>
          <input type="number" min="0" step="10" value={slot.max_price ?? ''}
                 onChange={ustaw('max_price')} className={poleKlasa} />
        </label>
        <label className="block">
          {/* Sama „Moc (W)" — dopisek „dokładna" łamał etykietę do dwóch linii
              i rozjeżdżał pola w rzędzie. Semantykę niesie placeholder i tooltip. */}
          <span className="block text-[11px] text-gray-500 mb-1">Moc (W)</span>
          <input type="number" min="0" step="5" value={slot.power ?? ''} placeholder="np. 500"
                 title="Dokładna moc modułu: 500 znajdzie tylko moduły 500 W"
                 onChange={ustaw('power')} className={poleKlasa} />
        </label>
      </div>

      <div>
        <span className="block text-[11px] text-gray-500 mb-1">Rodzaj</span>
        <div className="flex flex-wrap gap-1">
          {TYPY_MODULOW.map((typ) => {
            const aktywny = (slot.types || []).includes(typ)
            return (
              <button key={typ} type="button"
                      onClick={() => onChange({ ...slot, types: przelaczTyp(slot.types || [], typ) })}
                      className={`px-2 py-1 rounded-lg text-[11px] font-medium border transition-colors ${
                        aktywny ? 'bg-blue-600 border-blue-600 text-white'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'}`}>
                {typ}
              </button>
            )
          })}
        </div>
      </div>

      <details>
        <summary className="text-[11px] font-semibold text-blue-600 cursor-pointer select-none">
          Zaawansowane
        </summary>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <label className="block">
            <span className="block text-[11px] text-gray-500 mb-1">Gwarancja min. (lata)</span>
            <input type="number" min="0" value={slot.min_product_warranty ?? ''}
                   onChange={ustaw('min_product_warranty')} className={poleKlasa} />
          </label>
          <label className="block">
            <span className="block text-[11px] text-gray-500 mb-1">Sprawność min. (%)</span>
            <input type="number" min="0" step="0.1" value={slot.min_efficiency ?? ''}
                   onChange={ustaw('min_efficiency')} className={poleKlasa} />
          </label>
          <label className="block">
            <span className="block text-[11px] text-gray-500 mb-1">Długość maks. (mm)</span>
            <input type="number" min="0" step="10" value={slot.max_length ?? ''}
                   onChange={ustaw('max_length')} className={poleKlasa} />
          </label>
          <label className="block">
            <span className="block text-[11px] text-gray-500 mb-1">Kolor ramy</span>
            <select value={slot.frame_colour ?? ''} onChange={ustaw('frame_colour')}
                    className={poleKlasa}>
              <option value="">Dowolny</option>
              <option value="Czarna rama">Czarna rama</option>
              <option value="Srebrna rama">Srebrna rama</option>
            </select>
          </label>
        </div>
      </details>

      {szukam
        ? <p className="text-xs text-gray-400 py-6 text-center">Szukam najtańszego modułu…</p>
        : oferta
          ? <KafelekModulu oferta={oferta} polecany={pusty} />
          : (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5 leading-5">
              Żaden moduł nie spełnia tych warunków przy wymaganej dostępności.
              Poluzuj kryteria albo obniż minimalny stan.
            </p>
          )}
    </div>
  )
}

// ── Sekcja główna ────────────────────────────────────────────────────────────

const KitPreferencesSection = ({ setFlash }) => {
  const [prefs, setPrefs] = useState(null)
  const [opcje, setOpcje] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [aktywna, setAktywna] = useState(ZAKLADKI[0].id)
  // Ref na najnowszy stan do zapisu — bez niego szybkie „zmień i od razu zapisz"
  // wysłałoby stan sprzed ostatniej zmiany.
  const prefsRef = useRef(null)
  prefsRef.current = prefs

  useEffect(() => {
    let cancelled = false
    Promise.all([api.getKitPreferences(), api.getKitPreferenceOptions()]).then(([p, o]) => {
      if (cancelled) return
      if (p.ok) {
        const dane = p.data
        // Puste sloty na start: trzy, do zmiany strzałkami. Trzy, bo tyle
        // pozycji miały ręczne zestawienia „na czym wyceniać" — sprawdzony rozmiar.
        if (!dane.module_slots || dane.module_slots.length === 0) {
          dane.module_slots = [{ ...PUSTY_SLOT }, { ...PUSTY_SLOT }, { ...PUSTY_SLOT }]
        }
        setPrefs(dane)
      } else setError(p.error)
      if (o.ok) setOpcje(o.data)
    })
    return () => { cancelled = true }
  }, [])

  // Podświetlenie zakładki idzie za przewijaniem, a nie tylko za kliknięciem —
  // inaczej pasek kłamie o tym, gdzie użytkownik jest, gdy zjedzie kółkiem.
  //
  // Liczone wprost z pozycji kart, a NIE IntersectionObserverem: obserwator
  // dostaje w wywołaniu tylko te sekcje, które właśnie zmieniły stan widoczności,
  // więc przy przejściu między dwiema długimi kartami żadna nic nie zgłasza
  // i pasek zostaje na poprzedniej zakładce. Sprawdzone na localhoście —
  // podświetlenie zacinało się na „Sklepach" mimo przewinięcia do modułów.
  //
  // Aktywna jest OSTATNIA karta, której górna krawędź minęła próg tuż pod
  // przyklejonym paskiem. Cztery odczyty pozycji na zdarzenie przewijania,
  // nasłuch pasywny — nie blokuje przewijania.
  const gotowe = Boolean(prefs && opcje)
  useEffect(() => {
    if (!gotowe) return undefined
    const PROG = 160
    const przelicz = () => {
      let biezaca = ZAKLADKI[0].id
      ZAKLADKI.forEach(({ id }) => {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top <= PROG) biezaca = id
      })
      setAktywna(biezaca)
    }
    przelicz()
    window.addEventListener('scroll', przelicz, { passive: true })
    return () => window.removeEventListener('scroll', przelicz)
  }, [gotowe])

  // Tryb modułów gaśnie razem z tym, na czym się opiera. Bez tego użytkownik,
  // który zaznaczył „tylko wybrane moduły", a potem wyczyścił kryteria, zapisałby
  // preferencję znaczącą „żaden moduł nie pasuje" — asystent nie zwróciłby ani
  // jednego zestawu i nie byłoby wiadomo dlaczego.
  useEffect(() => {
    if (!prefs) return
    const tryb = prefs.module_scope || 'any'
    if (tryb === 'any') return
    const maProducentow = (prefs.module_manufacturers || []).length > 0 || maWybrane(prefs, 'panels')
    const maSloty = (prefs.module_slots || []).some((s) => !slotPusty(s))
    if ((tryb === 'manufacturers' && !maProducentow) || (tryb === 'slots' && !maSloty)) {
      setPrefs((p) => ({ ...p, module_scope: 'any' }))
    }
  }, [prefs])

  const skoczDo = (id) => {
    const el = document.getElementById(id)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setAktywna(id)
  }

  const zapisz = async () => {
    setBusy(true); setError('')
    const res = await api.updateKitPreferences(prefsRef.current)
    setBusy(false)
    if (res.ok) {
      const dane = res.data
      if (!dane.module_slots || dane.module_slots.length === 0) {
        dane.module_slots = [{ ...PUSTY_SLOT }, { ...PUSTY_SLOT }, { ...PUSTY_SLOT }]
      }
      setPrefs(dane)
      setFlash('Preferencje doboru zapisane.')
    } else setError(res.error)
  }

  if (!prefs || !opcje) return <p className="text-sm text-gray-500">Wczytywanie…</p>

  const ustaw = (pole) => (wartosc) => setPrefs((p) => ({ ...p, [pole]: wartosc }))
  const ustawWybrane = (kat) => (wartosc) => setPrefs((p) => ({ ...p, wybrane: { ...(p.wybrane || {}), [kat]: wartosc } }))
  const sklepyTylko = prefs.seller_scope === 'only'
  const iluSlotow = prefs.module_slots.length

  const zmienLiczbeSlotow = (n) => {
    const cel = Math.max(1, Math.min(6, n))
    setPrefs((p) => {
      const sloty = p.module_slots.slice(0, cel)
      while (sloty.length < cel) sloty.push({ ...PUSTY_SLOT })
      return { ...p, module_slots: sloty }
    })
  }

  // Sloty szukają wśród wybranych producentów modułów; sklepy zawężają wynik
  // TYLKO przy włączonym trybie „tylko moje sklepy" — samo wpisanie sklepów
  // bez tej flagi znaczy „preferuj", a preferencja nie powinna ukrywać ofert.
  const sklepyDlaSlotow = sklepyTylko ? prefs.sellers : []

  return (
    <div className="space-y-5">
      <SectionTitle hint="Na czym pracujesz na co dzień. Te ustawienia będą podpowiadać
                          filtry w asystencie doboru i kolejność ofert.">
        Preferencje doboru
      </SectionTitle>

      <PasekZakladek aktywna={aktywna} onWybor={skoczDo} />

      <KartaSekcji
        id="falowniki" Icon={IkonaFalownika}
        tytul="Falowniki"
        opis="Marki, na których pracujecie: szkolenia, serwis, uprawnienia."
      >
        <WyborNazw
          dostepne={opcje.inverter_manufacturers}
          wybrane={prefs.inverter_manufacturers}
          onChange={ustaw('inverter_manufacturers')}
          placeholder="Szukaj producenta…"
          logoPath={getInverterManufacturerLogoPath}
        />
        <WyborSeriiIModeli kategoria="inverter" producenci={opcje.inverter_manufacturers} przyklad="Deye SUN-10K-SG04LP3" logoPath={getInverterManufacturerLogoPath}
                           wartosc={prefs.wybrane?.inverter} onChange={ustawWybrane('inverter')} />
      </KartaSekcji>

      <KartaSekcji
        id="magazyny" Icon={Battery50Icon}
        tytul="Magazyny energii"
        opis="Baterie mają własne marki, Pylontech czy Dyness nie robią falowników."
      >
        <WyborNazw
          dostepne={opcje.battery_manufacturers}
          wybrane={prefs.battery_manufacturers}
          onChange={ustaw('battery_manufacturers')}
          placeholder="Szukaj producenta…"
          logoPath={getMagazineManufacturerLogoPath}
        />
        <WyborSeriiIModeli kategoria="battery" producenci={opcje.battery_manufacturers} przyklad="Pylontech US5000" logoPath={getMagazineManufacturerLogoPath}
                           wartosc={prefs.wybrane?.battery} onChange={ustawWybrane('battery')} />

        {/* Przełącznik dotyczy OBU list producentów, dlatego siedzi pod drugą
            z nich i mówi to wprost. Osobna karta tylko na jeden checkbox byłaby
            czwartą zakładką bez treści. */}
        {(prefs.inverter_manufacturers.length > 0 || prefs.battery_manufacturers.length > 0
          || maWybrane(prefs, 'inverter') || maWybrane(prefs, 'battery')) && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <CheckboxTylko checked={prefs.manufacturers_only} onChange={ustaw('manufacturers_only')}>
              Dobieraj <strong>TYLKO</strong> zestawy z tymi markami, seriami i modelami{' '}
              <span className="text-gray-400">
                (dotyczy falowników i magazynów; po odznaczeniu asystent dobiera też
                inne marki, a z Twoich powstaje osobny wariant „Preferowane wybory")
              </span>
            </CheckboxTylko>
          </div>
        )}
      </KartaSekcji>

      <KartaSekcji
        id="sklepy" Icon={BuildingStorefrontIcon}
        tytul="Sklepy stałej współpracy"
        opis="Sklepy i hurtownie, w których się zaopatrujecie."
      >
        <WyborNazw
          bezPelnejListy
          dostepne={opcje.sellers}
          wybrane={prefs.sellers}
          onChange={(sklepy) => setPrefs((p) => ({
            ...p, sellers: sklepy,
            // Pusta lista wraca do trybu neutralnego — „tylko moje sklepy"
            // bez sklepów ukryłoby cały katalog.
            seller_scope: sklepy.length === 0 ? 'any' : (p.seller_scope === 'any' ? 'prefer' : p.seller_scope),
          }))}
          placeholder="Szukaj sklepu…"
        />
        {prefs.sellers.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <CheckboxTylko checked={sklepyTylko}
                           onChange={(v) => ustaw('seller_scope')(v ? 'only' : 'prefer')}>
              Wyświetlaj produkty <strong>TYLKO</strong> tych sklepów{' '}
              <span className="text-gray-400">
                (po odznaczeniu pokazujemy wszystkie sklepy, Twoje w pierwszej kolejności)
              </span>
            </CheckboxTylko>
          </div>
        )}
      </KartaSekcji>

      <KartaSekcji
        id="moduly" Icon={IkonaModulu}
        tytul="Moduły"
        opis="Moduły rotują tygodniowo, więc najlepiej działają kryteria, a resztę dobieramy
              z aktualnych cen. Gdy kupujecie stale tę samą serię albo model, wskażcie je niżej."
      >
        <WyborNazw
          tytul="Producenci modułów"
          dostepne={opcje.module_manufacturers}
          wybrane={prefs.module_manufacturers}
          onChange={ustaw('module_manufacturers')}
          placeholder="Szukaj producenta…"
          logoPath={getPanelManufacturerLogoPath}
        />
        <WyborSeriiIModeli kategoria="panels" producenci={opcje.module_manufacturers} przyklad="JA Solar JAM54D40-450" logoPath={getPanelManufacturerLogoPath}
                           wartosc={prefs.wybrane?.panels} onChange={ustawWybrane('panels')} />

        <div className="mt-5">
          <div className="flex items-end justify-between flex-wrap gap-3">
            {/* Runda 5 (2026-10-12): jeden nagłówek zamiast „Wybrane moduły” z akapitem o kryteriach. */}
            <div>
              <p className="text-sm font-semibold text-gray-900">Znajdź moduły do stałej oferty</p>
              {(prefs.module_manufacturers.length > 0 || sklepyTylko) && (
                <p className="text-xs text-gray-500 leading-5 mt-0.5 max-w-lg">
                  {[prefs.module_manufacturers.length > 0 && 'Szukamy wśród producentów wybranych wyżej.',
                    sklepyTylko && 'Tylko w Twoich sklepach.'].filter(Boolean).join(' ')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2">
                <span className="text-[11px] text-gray-500">Liczba modułów</span>
                <span className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden">
                  <button type="button" onClick={() => zmienLiczbeSlotow(iluSlotow - 1)}
                          aria-label="Mniej modułów" className="p-1.5 text-gray-500 hover:bg-gray-50">
                    <MinusIcon className="w-3.5 h-3.5" />
                  </button>
                  <input type="number" min="1" max="6" value={iluSlotow}
                         onChange={(e) => zmienLiczbeSlotow(parseInt(e.target.value, 10) || 1)}
                         className="w-10 text-center text-sm border-x border-gray-200 py-1
                                    focus:outline-none" />
                  <button type="button" onClick={() => zmienLiczbeSlotow(iluSlotow + 1)}
                          aria-label="Więcej modułów" className="p-1.5 text-gray-500 hover:bg-gray-50">
                    <PlusIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              </label>
              <label className="flex items-center gap-2">
                <span className="text-[11px] text-gray-500">Min. stan (szt.)</span>
                <input type="number" min="0" step="10" value={prefs.module_min_stock}
                       onChange={(e) => ustaw('module_min_stock')(Math.max(0, parseInt(e.target.value, 10) || 0))}
                       className="w-16 px-2 py-1 rounded-lg border border-gray-200 text-sm text-center
                                  focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </label>
            </div>
          </div>

          <div className="mt-3 grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {prefs.module_slots.map((slot, i) => (
              <SlotModulu
                key={i} numer={i + 1} slot={slot}
                onChange={(nowy) => setPrefs((p) => ({
                  ...p,
                  module_slots: p.module_slots.map((s, j) => (j === i ? nowy : s)),
                }))}
                producenci={prefs.module_manufacturers}
                sklepy={sklepyDlaSlotow}
                minSztuk={prefs.module_min_stock}
              />
            ))}
          </div>
        </div>

        {/* Tryb NA KOŃCU karty, pod slotami: obie opcje odnoszą się do rzeczy
            ustawionych wyżej (producenci, kryteria modułów), a decyzja „jak
            twardo" ma sens dopiero, gdy jest co zawężać. */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <WyborTrybuModulow
            tryb={prefs.module_scope || 'any'}
            onChange={ustaw('module_scope')}
            maProducentow={prefs.module_manufacturers.length > 0 || maWybrane(prefs, 'panels')}
            maSloty={prefs.module_slots.some((s) => !slotPusty(s))}
          />
        </div>
      </KartaSekcji>

      <ErrorText>{error}</ErrorText>
      <Button onClick={zapisz} busy={busy}>Zapisz preferencje</Button>
    </div>
  )
}

export default KitPreferencesSection
